import { useEffect, useState } from "react";
import { Truck, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { StationResultProps } from "@/lib/type";
import {
  checkDeliveryAvailability,
  DeliveryAvailability,
  DeliverySelection,
} from "@/features/delivery/api/deliveryApi";
import DeliveryLeg from "@/features/delivery/components/DeliveryLeg";
import { meetsMinimumRentalDuration } from "@/features/delivery/lib/deliveryCalculations.js";

interface DeliveryServicePanelProps {
  pickupStation?: StationResultProps;
  returnStation?: StationResultProps;
  pickupScheduledAt: string;
  returnScheduledAt: string;
  currency: string;
  currencySymbol: string;
  apiUrl?: string;
  value?: DeliverySelection[] | null;
  onChange: (selections: DeliverySelection[]) => void;
}

type LegAvailability = Record<string, DeliveryAvailability>;

/** Limited-time delivery & collection promotion. */
const PROMO_END = "31 December 2026";

export default function DeliveryServicePanel({
  pickupStation,
  returnStation,
  pickupScheduledAt,
  returnScheduledAt,
  currencySymbol,
  apiUrl,
  value,
  onChange,
}: DeliveryServicePanelProps) {
  const [availability, setAvailability] = useState<LegAvailability>({});
  const [expanded, setExpanded] = useState<boolean>(Boolean(value && value.length));
  const [legs, setLegs] = useState<{ pickup: DeliverySelection | null; return: DeliverySelection | null }>(() => {
    // Restore from the parent's saved state when remounting (e.g. navigating back from step 4).
    const pickup = value?.find((s) => s.type === "pickup") ?? null;
    const ret = value?.find((s) => s.type === "return") ?? null;
    return { pickup, return: ret };
  });

  useEffect(() => {
    const checks = [
      pickupStation && checkDeliveryAvailability({
        station_id: pickupStation.rw_station_id,
        type: "pickup",
        scheduled_at: pickupScheduledAt,
        rental_start_at: pickupScheduledAt,
        rental_end_at: returnScheduledAt,
        apiUrl,
      }).then((result) => ["pickup", result] as const).catch((err) => ["pickup", err as DeliveryAvailability] as const),
      returnStation && checkDeliveryAvailability({
        station_id: returnStation.rw_station_id,
        type: "return",
        scheduled_at: returnScheduledAt,
        rental_start_at: pickupScheduledAt,
        rental_end_at: returnScheduledAt,
        apiUrl,
      }).then((result) => ["return", result] as const).catch((err) => ["return", err as DeliveryAvailability] as const),
    ].filter(Boolean) as Promise<readonly [string, DeliveryAvailability]>[];

    Promise.all(checks).then((results) => {
      setAvailability(Object.fromEntries(results.map(([key, result]) => {
        const minimumDays = result.min_rental_days ?? result.min_lead_days ?? 0;
        const meetsMinimum = meetsMinimumRentalDuration(pickupScheduledAt, returnScheduledAt, minimumDays);

        return [key, result.available && !meetsMinimum
          ? {
              ...result,
              available: false,
              error_code: "RENTAL_PERIOD_TOO_SHORT",
              message: `Delivery service requires a rental period of at least ${minimumDays} day${minimumDays === 1 ? "" : "s"}.`,
            }
          : result];
      })));
    });
  }, [apiUrl, pickupScheduledAt, pickupStation, returnScheduledAt, returnStation]);

  useEffect(() => {
    const pickupUnavailable = "pickup" in availability && !availability.pickup?.available;
    const returnUnavailable = "return" in availability && !availability.return?.available;

    if ((!pickupUnavailable || !legs.pickup) && (!returnUnavailable || !legs.return)) {
      return;
    }

    const next = {
      pickup: pickupUnavailable ? null : legs.pickup,
      return: returnUnavailable ? null : legs.return,
    };

    setLegs(next);
    onChange([next.pickup, next.return].filter(Boolean) as DeliverySelection[]);
  }, [availability, legs, onChange]);

  const updateLeg = (leg: "pickup" | "return", selection: DeliverySelection | null) => {
    setLegs((prev) => {
      const next = { ...prev, [leg]: selection };
      onChange([next.pickup, next.return].filter(Boolean) as DeliverySelection[]);
      return next;
    });
  };

  const pickupAvailable = availability.pickup?.available;
  const returnAvailable = availability.return?.available;

  if (!pickupAvailable && !returnAvailable) {
    return null;
  }

  const selectedCount = [legs.pickup, legs.return].filter(Boolean).length;

  return (
    <section className={`rounded-md border p-4 ${expanded ? "border-neutral-200 bg-white" : "border-green-300 bg-green-50"}`}>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 text-left"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-100 text-green-700">
          <Truck className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="flex items-center gap-2 text-base font-semibold text-green-800">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            We're available for delivery &amp; collection now!
          </h3>
          <p className="text-sm text-neutral-600">
            {selectedCount > 0
              ? `${selectedCount} option${selectedCount > 1 ? "s" : ""} added — tap to review or change.`
              : `Have the car delivered to your door and collected from you. Limited-time offer until ${PROMO_END}. Tap to see options.`}
          </p>
        </div>
        <span className="ml-2 inline-flex items-center gap-1 text-sm font-medium text-green-700" aria-hidden="true">
          {expanded ? "Hide" : "Show"}
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            <p className="font-semibold">Delivery &amp; collection now available</p>
            <p>
              Skip the branch — we can bring the car to you and collect it afterwards. You can choose
              <strong> delivery, collection, or both</strong>. Offer valid until {PROMO_END}.
            </p>
          </div>

          {pickupAvailable && (
            <DeliveryLeg
              type="pickup"
              station={pickupStation}
              scheduledAt={pickupScheduledAt}
              rentalStartAt={pickupScheduledAt}
              rentalEndAt={returnScheduledAt}
              currencySymbol={currencySymbol}
              apiUrl={apiUrl}
              availability={availability.pickup}
              initialValue={legs.pickup}
              onChange={(selection) => updateLeg("pickup", selection)}
            />
          )}

          {returnAvailable && (
            <DeliveryLeg
              type="return"
              station={returnStation}
              scheduledAt={returnScheduledAt}
              rentalStartAt={pickupScheduledAt}
              rentalEndAt={returnScheduledAt}
              currencySymbol={currencySymbol}
              apiUrl={apiUrl}
              availability={availability.return}
              initialValue={legs.return}
              onChange={(selection) => updateLeg("return", selection)}
            />
          )}
        </div>
      )}
    </section>
  );
}
